<template>
  <div class="flex flex-col gap-4">
    <h2 v-if="detailsQuery.data.value" class="text-xl font-semibold">
      {{ t('title', { number: detailsQuery.data.value.batchNumber }) }}
    </h2>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner class="text-hazel-600" />
    </div>

    <template v-else>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end">
        <Input v-model="search" :placeholder="t('search')" variant="ghost" />
        <Select
          v-model="matchStatusFilter"
          :label="t('matchStatus')"
          :options="matchStatusOptions"
          empty-value-to="null"
        />
        <Select
          v-model="typeFilter"
          :label="t('type')"
          :options="typeOptions"
          empty-value-to="null"
        />
      </div>

      <div :class="table({ class: 'grid-cols-[repeat(6,minmax(max-content,1fr))]' })">
        <div :class="tableHeader()">
          <div :class="tableCell()">{{ t('type') }}</div>
          <div :class="tableCell()">{{ t('paymentMethod') }}</div>
          <div :class="tableCell()">{{ t('references') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('grossAmount') }}</div>
          <div :class="tableCell()">{{ t('matchStatus') }}</div>
          <div :class="tableCell()">{{ t('payment') }}</div>
        </div>
        <div v-for="line in filteredLines" :key="line.id" :class="tableRow()">
          <div :class="tableCell()">{{ line.type }}</div>
          <div :class="tableCell()">{{ line.paymentMethod || '-' }}</div>
          <div :class="tableCell()">
            <div class="flex flex-col gap-0.5 font-mono text-[10px] leading-snug">
              <div v-if="line.pspReference">
                <span class="text-obsidian-400">{{ t('refPsp') }}</span>
                <a
                  :href="adyenPaymentListUrl(line.pspReference)"
                  class="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ line.pspReference }}
                </a>
              </div>
              <div v-if="line.merchantReference">
                <span class="text-obsidian-400">{{ t('refMerchant') }}</span>
                {{ line.merchantReference }}
              </div>
              <div v-if="line.modificationPspReference">
                <span class="text-obsidian-400">{{ t('refModPsp') }}</span>
                <a
                  :href="adyenPaymentListUrl(line.modificationPspReference)"
                  class="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ line.modificationPspReference }}
                </a>
              </div>
              <div
                v-if="
                  line.modificationMerchantReference &&
                  line.modificationMerchantReference !== '<auto>'
                "
              >
                <span class="text-obsidian-400">{{ t('refModMerchant') }}</span>
                {{ line.modificationMerchantReference }}
              </div>
              <div
                v-if="
                  !line.pspReference &&
                  !line.merchantReference &&
                  !line.modificationPspReference &&
                  (!line.modificationMerchantReference ||
                    line.modificationMerchantReference === '<auto>')
                "
              >
                -
              </div>
            </div>
          </div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(line.grossAmount) }}
          </div>
          <div :class="tableCell()">
            {{ line.matchStatus ? t(`matchStatuses.${line.matchStatus}`) : '-' }}
          </div>
          <div :class="tableCell()">
            <template v-if="line.payment">
              <div class="flex items-center gap-1.5">
                <div class="min-w-0">
                  <router-link
                    v-if="line.payment.booking?.id"
                    :to="{ name: 'bookingEdit', params: { id: line.payment.booking.id } }"
                    class="text-link"
                  >
                    {{
                      line.payment.booking.bookingNumber
                        ? t('bookingLabel', { number: line.payment.booking.bookingNumber })
                        : t('paymentLinked')
                    }}
                  </router-link>
                  <span v-else>{{ t('paymentLinked') }}</span>
                  <span class="ml-1 text-obsidian-500">
                    {{ formatCurrency(line.payment.amount) }}
                  </span>
                </div>
                <CVButton
                  variant="ghost"
                  size="iconSm"
                  :title="t('relink')"
                  :aria-label="t('relink')"
                  @click="openAssign(line.id)"
                >
                  <Link2Icon class="size-3.5" />
                </CVButton>
              </div>
            </template>
            <CVButton v-else size="sm" @click="openAssign(line.id)">
              {{ t('assign') }}
            </CVButton>
          </div>
        </div>
      </div>

      <EntityListExceptions
        :status="status"
        :error="error as any"
        :items-count="filteredLines.length"
        :no-items-text="lines.length ? t('noMatchingLines') : t('noLines')"
        small
      />
    </template>

    <PaymentPickerModal
      v-model="showPicker"
      v-model:search="pickerSearch"
      :items="pickerItems"
      :mark-as-suggestions="!pickerSearchDebounced"
      :context-references="pickerContextReferences"
      :is-loading="candidatesQuery.isLoading.value"
      :status="candidatesQuery.status.value"
      :error="candidatesQuery.error.value"
      :is-confirming="assignMutation.isPending.value"
      :has-next-page="candidatesQuery.hasNextPage.value"
      :is-fetching-next-page="candidatesQuery.isFetchingNextPage.value"
      @select="handleAssign"
      @load-more="() => candidatesQuery.fetchNextPage()"
    />
  </div>
</template>

<script lang="ts" setup>
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import PaymentPickerModal, {
  type PaymentPickerContextReference,
  type PaymentPickerItem,
} from '@/components/payments/PaymentPickerModal.vue';
import {
  useAssignSettlementPayment,
  useSettlementBatchDetails,
  useSettlementPaymentCandidates,
} from '@/queries/use-settlement';
import { formatCurrency } from '@/utils/format-numbers';
import { table, tableCell, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import { refDebounced } from '@vueuse/core';
import { Link2Icon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createToast } from 'mosha-vue-toastify';
import { uniq } from 'lodash';
import type { SettlementDetailLineDto } from '@carvia/ros-client-types';

const props = defineProps<{
  batchId: string;
}>();

const { t } = useI18n();
const showPicker = ref(false);
const assignDetailId = ref<string | null>(null);
const pickerSearch = ref('');
const pickerSearchDebounced = refDebounced(pickerSearch, 300);
const search = ref('');
const searchDebounced = refDebounced(search, 200);
const matchStatusFilter = ref<string | null>(null);
const typeFilter = ref<string | null>(null);

const detailsQuery = useSettlementBatchDetails(() => props.batchId);
const candidatesQuery = useSettlementPaymentCandidates(
  assignDetailId,
  pickerSearchDebounced,
  () => showPicker.value && !!assignDetailId.value,
);
const assignMutation = useAssignSettlementPayment();

const lines = computed(() => detailsQuery.data.value?.lines ?? []);
const isLoading = computed(() => detailsQuery.isLoading.value);
const status = computed(() => detailsQuery.status.value);
const error = computed(() => detailsQuery.error.value);
const pickerItems = computed<PaymentPickerItem[]>(() =>
  (candidatesQuery.data.value?.pages.flatMap((page) => page.items) ?? []).map((payment) => ({
    id: payment.id,
    amount: payment.amount,
    date: payment.date,
    paidDate: payment.paidDate,
    counterpartyType: payment.counterpartyType,
    paymentType: payment.paymentType,
    referenceId: payment.referenceId,
    bookingNumber: payment.booking?.bookingNumber ?? null,
  })),
);

const assignLine = computed(
  () => lines.value.find((line) => line.id === assignDetailId.value) ?? null,
);

const pickerContextReferences = computed((): PaymentPickerContextReference[] => {
  const line = assignLine.value;
  if (!line) return [];
  return detailReferenceItems(line);
});

const detailReferenceItems = (line: SettlementDetailLineDto): PaymentPickerContextReference[] => {
  const items: PaymentPickerContextReference[] = [];
  if (line.pspReference) items.push({ label: t('refPsp'), value: line.pspReference });
  if (line.merchantReference)
    items.push({ label: t('refMerchant'), value: line.merchantReference });
  if (line.modificationPspReference) {
    items.push({ label: t('refModPsp'), value: line.modificationPspReference });
  }
  if (line.modificationMerchantReference && line.modificationMerchantReference !== '<auto>') {
    items.push({ label: t('refModMerchant'), value: line.modificationMerchantReference });
  }
  return items;
};

const adyenPaymentListUrl = (pspReference: string) => {
  const host =
    import.meta.env.VITE_ENVIRONMENT === 'production' ? 'ca-live.adyen.com' : 'ca-test.adyen.com';
  return `https://${host}/ca/ui/payments/payment-list/${encodeURIComponent(pspReference)}/`;
};

const matchStatusOptions = computed(() => [
  { label: t('allMatchStatuses'), value: null },
  { label: t('matchStatuses.MATCHED'), value: 'MATCHED' },
  { label: t('matchStatuses.AMOUNT_MISMATCH'), value: 'AMOUNT_MISMATCH' },
  { label: t('matchStatuses.NOT_FOUND_IN_SYSTEM'), value: 'NOT_FOUND_IN_SYSTEM' },
]);

const typeOptions = computed(() => [
  { label: t('allTypes'), value: null },
  ...uniq(lines.value.map((line) => line.type))
    .sort()
    .map((type) => ({ label: type, value: type })),
]);

const filteredLines = computed(() => {
  const q = searchDebounced.value.trim().toLowerCase();
  return lines.value.filter((line) => {
    if (typeFilter.value && line.type !== typeFilter.value) return false;

    if (matchStatusFilter.value && line.matchStatus !== matchStatusFilter.value) {
      return false;
    }

    if (!q) return true;
    const haystack = [
      line.type,
      line.paymentMethod,
      line.pspReference,
      line.merchantReference,
      line.modificationPspReference,
      line.modificationMerchantReference,
      line.payment?.referenceId,
      line.payment?.booking?.bookingNumber != null
        ? String(line.payment.booking.bookingNumber)
        : null,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
});

const resetFilters = () => {
  search.value = '';
  matchStatusFilter.value = null;
  typeFilter.value = null;
  showPicker.value = false;
  assignDetailId.value = null;
  pickerSearch.value = '';
};

watch(
  () => props.batchId,
  () => resetFilters(),
);

watch(showPicker, (open) => {
  if (!open) {
    assignDetailId.value = null;
    pickerSearch.value = '';
  }
});

const openAssign = (detailId: string) => {
  assignDetailId.value = detailId;
  showPicker.value = true;
};

const handleAssign = async (payment: PaymentPickerItem) => {
  if (!assignDetailId.value) return;
  try {
    await assignMutation.mutateAsync({
      detailId: assignDetailId.value,
      paymentId: payment.id,
    });
    showPicker.value = false;
    assignDetailId.value = null;
    createToast(t('assignSuccess'), { position: 'bottom-right', type: 'success' });
  } catch (e: any) {
    createToast(e?.response?.data?.message ?? t('assignError'), {
      position: 'bottom-right',
      type: 'danger',
    });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Settlement batch {number}",
    "search": "Search PSP, merchant ref, booking, method…",
    "allMatchStatuses": "All match statuses",
    "allTypes": "All types",
    "type": "Type",
    "paymentMethod": "Method",
    "references": "References",
    "refPsp": "PSP:",
    "refMerchant": "Merch:",
    "refModPsp": "Mod PSP:",
    "refModMerchant": "Mod merch:",
    "grossAmount": "Amount",
    "matchStatus": "Match",
    "payment": "Payment",
    "assign": "Assign",
    "relink": "Relink",
    "paymentLinked": "Linked payment",
    "bookingLabel": "Booking {number}",
    "noLines": "No PSP lines in this batch",
    "noMatchingLines": "No lines match the filters",
    "assignSuccess": "Payment linked",
    "assignError": "Could not link payment",
    "matchStatuses": {
      "MATCHED": "Matched",
      "AMOUNT_MISMATCH": "Amount mismatch",
      "NOT_FOUND_IN_SYSTEM": "Not found"
    }
  },
  "de": {
    "title": "Settlement-Batch {number}",
    "search": "PSP, Merchant-Ref, Buchung, Methode suchen…",
    "allMatchStatuses": "Alle Abgleich-Status",
    "allTypes": "Alle Typen",
    "type": "Typ",
    "paymentMethod": "Methode",
    "references": "Referenzen",
    "refPsp": "PSP:",
    "refMerchant": "Merch:",
    "refModPsp": "Mod-PSP:",
    "refModMerchant": "Mod-Merch:",
    "grossAmount": "Betrag",
    "matchStatus": "Abgleich",
    "payment": "Zahlung",
    "assign": "Verknüpfen",
    "relink": "Neu verknüpfen",
    "paymentLinked": "Verknüpfte Zahlung",
    "bookingLabel": "Buchung {number}",
    "noLines": "Keine PSP-Zeilen in diesem Batch",
    "noMatchingLines": "Keine Zeilen passen zu den Filtern",
    "assignSuccess": "Zahlung verknüpft",
    "assignError": "Zahlung konnte nicht verknüpft werden",
    "matchStatuses": {
      "MATCHED": "Übereinstimmend",
      "AMOUNT_MISMATCH": "Betragsabweichung",
      "NOT_FOUND_IN_SYSTEM": "Nicht gefunden"
    }
  },
  "es": {
    "title": "Lote de liquidación {number}",
    "search": "Buscar PSP, ref. comercio, reserva, método…",
    "allMatchStatuses": "Todos los estados",
    "allTypes": "Todos los tipos",
    "type": "Tipo",
    "paymentMethod": "Método",
    "references": "Referencias",
    "refPsp": "PSP:",
    "refMerchant": "Comercio:",
    "refModPsp": "Mod PSP:",
    "refModMerchant": "Mod comercio:",
    "grossAmount": "Importe",
    "matchStatus": "Conciliación",
    "payment": "Pago",
    "assign": "Asignar",
    "relink": "Reasignar",
    "paymentLinked": "Pago vinculado",
    "bookingLabel": "Reserva {number}",
    "noLines": "No hay líneas PSP en este lote",
    "noMatchingLines": "Ninguna línea coincide con los filtros",
    "assignSuccess": "Pago vinculado",
    "assignError": "No se pudo vincular el pago",
    "matchStatuses": {
      "MATCHED": "Coincide",
      "AMOUNT_MISMATCH": "Importe no coincide",
      "NOT_FOUND_IN_SYSTEM": "No encontrado"
    }
  }
}
</i18n>
